/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import { STAGE_ACTION_REQUIRED } from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import { dateFieldDefault, emailDefault, ibanFieldDefault, isBiggerOrSameDateThan } from "../../../validationRules";
import addressFields from "../modules/addressFields";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import InputAdornment from "@mui/material/InputAdornment";
import mobileProviders from "../../../mobileProviders";
import _ from "lodash";
import { objectivesLawFirm } from "../modules/objectiveLawfirmValues";
import { arrayToSelectOptions } from "../../../formServiceFunctions";
import { getCommonContractLawCaseRejectionCategorySelectOptions } from "../contract/contractLawFormHelperService";
import React from "react";
import { UseForm } from "../../../../types/UseForm";
import DateChangeButtonsToolbar from "../../../../components/Validator/DateChangeButtonsToolbar";
import { extrajudicialInvoicingFields } from "../abstractLitigationCase/abstractLitigationInvoicingFields";
import {
  addProcessParticipant,
  caseInsurance,
  getNextInstanceSection,
  litigationCourt,
  opponentLawyer,
  processParticipantsOrganizations,
  processParticipantsPeople,
  prosecution,
} from "../abstractLitigationCase/fullFormDefinition";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { overwriteJudicialProcedure } from "./overwriteJudicialProcedure";
import { translate } from "../../../Translations/translatorService";
import { DataLeakContract } from "../../../../types/Products/ContractLaw/DataLeakContract";
import { LitigationUnion } from "../../../../types/LitigationUnion";
import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";
import { debtCollectionAgencyFields } from "../modules/debtCollectionFields";

export const fullFormDefinition = (product: DataLeakContract) => {
  const contractBaseCreated = {
    isHidden: ({ product }: { product: DataLeakContract }) => (product.stage || 0) > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Vertrag",
    elements: [...personFields({ path: `${product.productClassName}.client` })],
  };

  const contractBase = {
    isHidden: ({ product }: { product: DataLeakContract }) => (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Vertrag",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseCategory`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) > STAGE_ACTION_REQUIRED || product.caseCategory === "advisory",
        options: ({ product }: { product: DataLeakContract }) => {
          return product.caseCategory === "advisory"
            ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
            : [{ value: "enforceClaim" }, { value: "defendClaim" }];
        },
        alert: {
          onChange:
            "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
            "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
            "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
            "der Mandantschaft im Verfahren tatsächlich ändern?",
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.contractDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
      },
      ...personFields({ path: `${product.productClassName}.client` }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
      }),
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.client.birthDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.objectiveClient`,
        isDisabled: () => true,
        isHidden: ({ product }: { product: DataLeakContract }) => product.caseCategory !== "enforceClaim",
        options: () => [
          { value: "compensation" },
          { value: "reduction" },
          { value: "replacement" },
          { value: "repair" },
          { value: "withdrawal" },
          { value: "fulfillment" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.subjectOfDispute`,
        translationPath: `${product.productClassName}.subjectOfDispute.${product.caseCategory}`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
        options: () => [{ value: "money" }, { value: "goods" }, { value: "other" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientHasAlreadyClaimed`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: DataLeakContract }) => product.caseCategory !== "enforceClaim",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientHasAlreadyClaimed`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDateClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: DataLeakContract }) => product.caseCategory !== "enforceClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDeadlineClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: DataLeakContract }) =>
          product.caseCategory !== "enforceClaim" || !product.clientHasAlreadyClaimed,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountClient`,
        isHidden: ({ product }: { product: DataLeakContract }) => product.subjectOfDispute !== "money",
        numberType: "float",
        fieldType: "money",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.overdueFees`,
        isHidden: ({ product }: { product: DataLeakContract }) =>
          product.subjectOfDispute !== "money" ||
          (product.caseCategory !== "defendClaim" && !product.clientHasAlreadyClaimed),
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            inputComponent: IbanInput,
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.defenseProcessStatus`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        isHidden: ({ product }: { product: DataLeakContract }) => product.caseCategory !== "defendClaim",
        options: () => [
          { value: "reminder" },
          { value: "debtCollection" },
          { value: "paymentOrder" },
          { value: "enforcementOrder" },
          { value: "lawsuit" },
          { value: "none" },
        ],
      },
    ],
  };

  const contractDetails = {
    isHidden: ({ product }: { product: DataLeakContract }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || 11,
    label: "Details zum Vertrag",
    elements: [
      {
        type: "ValidatorSelect",
        path: `dataLeakContract.mobileProviderOrganizationId`,
        isDisabled: () => true,
        options: () =>
          mobileProviders.map((provider) => {
            return { value: provider.value };
          }),
      },
      {
        type: "ValidatorTextField",
        path: `dataLeakContract.contractNumber`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_LAWSUIT,
      },
      {
        type: "ValidatorSelect",
        path: `dataLeakContract.salesChannel`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        options: () => [
          { value: "providerWebsite" },
          { value: "comparisonPortal" },
          { value: "localStore" },
          { value: "phone" },
          { value: "other" },
        ],
      },
    ],
  };

  const positiveEntriesSchufa = {
    label: "Schufa",
    elements: [
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.schufaDataCopyDate`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.schufaPositiveDataDate`,
        validators: [
          {
            validator: isBiggerOrSameDateThan,
            params: ["25.05.2018"],
            message: `Wert darf nicht vor dem 25.05.2018 liegen`,
          },
        ],
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.schufaDataCopyCheckedNegativeDate`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
      {
        type: "ValidatorTextField",
        path: `dataLeakContract.schufaPositiveDataServiceAccountNumber`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `dataLeakContract.schufaPositiveDataEntry`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
        additionalProps: {
          fullWidth: true,
          rows: 2,
          multiline: true,
        },
      },
    ],
  };

  const positiveEntriesCrif = {
    label: "Crif",
    elements: [
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.crifDataCopyDate`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.crifPositiveDataDate`,
        validators: [
          {
            validator: isBiggerOrSameDateThan,
            params: ["25.05.2018"],
            message: `Wert darf nicht vor dem 25.05.2018 liegen`,
          },
        ],
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
      {
        type: "ValidatorDateField",
        path: `dataLeakContract.crifDataCopyCheckedNegativeDate`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
      },
      {
        type: "ValidatorTextField",
        path: `dataLeakContract.crifTipNumber`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `dataLeakContract.crifPositiveDataEntry`,
        isDisabled: ({ product }: { product: DataLeakContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_PREPARE_LAWSUIT,
        additionalProps: {
          fullWidth: true,
          rows: 2,
          multiline: true,
        },
      },
    ],
  };

  const clientLegalRepresentative = {
    label: "Gesetzlicher Vertreter",
    isHidden: ({ product }: { product: DataLeakContract }) => !product.clientWasMinorAtContractDate,
    elements: [
      ...personFields({ path: `${product.productClassName}.clientLegalRepresentative` }),
      ...addressFields({ path: `${product.productClassName}.clientLegalRepresentative.residenceAddress` }),
    ],
  };

  const contractPartner = {
    isHidden: ({ product }: { product: DataLeakContract }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Gegenseite",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ContractualPartnerLegalEntityPicker",
        path: `${product.productClassName}.contractualPartnerLegalEntity.organization`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: DataLeakContract }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyLegalEntity",
      },
      ...personFields({
        path: `${product.productClassName}.contractualPartnerPerson`,
        allowNoGender: true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.telephone`,
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.contractualPartnerPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: DataLeakContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
      }),
    ],
  };

  const contractDescription = {
    isHidden: ({ product }: { product: DataLeakContract }) => (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED,
    label: "Beschreibung der Situation",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  const dataLeakSpecificJudicialProcedureFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.injunctiveReliefAmount`,
      isDisabled: () => false,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.compensationAmount`,
      isDisabled: () => false,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.declaratoryMotionAmount`,
      isDisabled: () => false,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseInSuspension`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.caseInSuspension`),
    },
  ];

  return {
    product: {
      sections: [contractBaseCreated, contractBase, contractDetails, contractDescription],
    },
    dataLeakContract: {
      sections: [
        {
          elements: [
            {
              type: "ValidatorSelect",
              path: "dataLeakContract.disablePositiveDataExpiryCheck",
              options: () => trueFalseDefaultOptions("dataLeakContract.disablePositiveDataExpiryCheck"),
            },
          ],
        },
        positiveEntriesSchufa,
        positiveEntriesCrif,
        {
          label: "Individuelle Betroffenheit Mandant",
          elements: [
            {
              type: "ValidatorTextField",
              path: "dataLeakContract.personalAffection.personalAffectionClient",
              isDisabled: () => true,
              additionalProps: {
                fullWidth: true,
                rows: 10,
                multiline: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: "dataLeakContract.personalAffection.personalAffectionLawFirm",
              isDisabled: () => false,
              additionalProps: {
                fullWidth: true,
                rows: 10,
                multiline: true,
              },
            },
          ],
        },
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirm`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
              options: ({ product }: { product: DataLeakContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmSecondary`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
              options: ({ product }: { product: DataLeakContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmTertiary`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
              options: ({ product }: { product: DataLeakContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        {
          label: "Außergerichtliches Verfahren",
          isHidden: ({ product }: { product: DataLeakContract }) =>
            (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED ||
            product.caseCategory === "advisory" ||
            product.previousInstanceCase,
          elements: [
            {
              type: "CaseRejectionCategorySelect",
              path: `${product.productClassName}.caseRejectionCategory`,
              options: getCommonContractLawCaseRejectionCategorySelectOptions,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product, values }: { product: DataLeakContract; values: UseForm["values"] }) =>
                values[product.productClassName].caseCategoryAssessment !== "reject",
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.assessmentRejectionText`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product, values }: { product: DataLeakContract; values: UseForm["values"] }) =>
                values[product.productClassName].caseCategoryAssessment !== "reject",
              additionalProps: {
                multiline: true,
                rows: 12,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.additionalIssueDescriptionText`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                product.advisoryHandling ||
                product.isLawsuitHandling ||
                ((product.stage || 0) >= stagesCase.STAGE_CHECKED && !product.additionalIssueDescriptionText),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.additionalClaimText`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                product.advisoryHandling ||
                product.isLawsuitHandling ||
                ((product.stage || 0) >= stagesCase.STAGE_CHECKED && !product.additionalClaimText),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.defectsDescriptionLawFirm`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                !_.has(product, "defectsDescriptionLawFirm") || product.caseCategory !== "enforceClaim",
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.extrajudicialCommunicationSent`,
              validators: dateFieldDefault,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) < stagesCase.STAGE_CHECKED ||
                (product.stage || 0) >= stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
              validators: dateFieldDefault,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED ||
                (product.stage || 0) >= stagesCase.STAGE_LAWSUIT,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
              additionalProps: {
                toolbar: DateChangeButtonsToolbar,
              },
            },
            {
              type: "ValidatorNumberField",
              path: `${product.productClassName}.claimedAmountLawFirm`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              fieldType: "money",
              numberType: "float",
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.extrajudicialReactionReceived`,
              validators: dateFieldDefault,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                product.extrajudicialReaction === "noReaction" ||
                (product.stage || 0) < stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
                (product.stage || 0) >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.extrajudicialReaction`,
              translationPath: `${product.productClassName}.extrajudicialReaction.${product.caseCategory}`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) < stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
                (product.stage || 0) >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
              options: () => [
                { value: "noReaction" },
                { value: "fullyAgreed" },
                { value: "partiallyAgreed" },
                { value: "disagreed" },
              ],
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.extrajudicialRecommendation`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
                product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
              options: ({ product }: { product: DataLeakContract }) =>
                product.caseCategory === "defendClaim"
                  ? [
                      { value: "agreementDone" },
                      { value: "insufficientEvidence" },
                      { value: "insufficientChancesOfSuccess" },
                      { value: "costBenefitConsiderations" },
                      { value: "lawsuit" },
                      { value: "individualText" },
                    ]
                  : [
                      { value: "agreementDone" },
                      { value: "insufficientEvidence" },
                      { value: "insufficientChancesOfSuccess" },
                      { value: "costBenefitConsiderations" },
                      { value: "anonymousOpponent" },
                      { value: "opponentUnableToPay" },
                      { value: "lawsuit" },
                      { value: "individualText" },
                    ],
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.extrajudicialRecommendationText`,
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                !(
                  product.stage === stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
                  (product.stage === stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
                    product.extrajudicialRecommendationText)
                ),
              isHidden: ({ product }: { product: DataLeakContract }) =>
                !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
                ((product.stage || 0) > stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
                  !product.extrajudicialRecommendationText),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.finalClaimStatusClient`, // Forderungsstatus nach Fristende aus Mandantensicht
              translationPath: `${product.productClassName}.finalClaimStatusClient.${product.caseCategory}`, // Forderungsstatus nach Fristende aus Mandantensicht
              helperText:
                "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
                "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
              isHidden: ({ product }: { product: DataLeakContract }) => product.advisoryHandling,
              options: () => [
                { value: "fullyAgreed" },
                { value: "partiallyAgreed" },
                { value: "notAgreed" },
                { value: "noReaction" },
              ],
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.finalProcessFeedbackClient`, // Rückmeldung Mandant
              translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`, // Rückmeldung Mandant
              helperText:
                "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
                "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
              isDisabled: ({ product }: { product: DataLeakContract }) =>
                (product.stage || 0) !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
              isHidden: ({ product }: { product: DataLeakContract }) =>
                product.advisoryHandling || product.finalClaimStatusClient === "fullyAgreed",
              options: () => [{ value: "agreement" }, { value: "lawsuit" }],
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.statementOfClaimSentDate`,
              validators: dateFieldDefault,
              isHidden: ({ product }: { product: DataLeakContract }) => product.caseCategory !== "enforceClaim",
            },
            ...extrajudicialInvoicingFields({ product }),
          ],
        },
        overwriteJudicialProcedure(product, dataLeakSpecificJudicialProcedureFields),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: DataLeakContract }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        contractPartner,
        caseInsurance(product),
        clientLegalRepresentative,
        debtCollectionAgencyFields(product),
        opponentLawyer(product),
        prosecution(product),
      ],
    },
    additionalProcessParticipants: {
      isHidden: () => product.advisoryHandling,
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      isHidden: () => product.advisoryHandling,
      sections: [
        litigationCourt(product, ["Amtsgericht", "Arbeitsgericht", "Landgericht", "Mahngericht", "Oberlandesgericht"]),
      ],
    },
  };
};
